import {useEffect, useCallback, useState, useMemo} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {connect} from 'react-redux'

import {useNavigate, useSearchParams} from 'react-router-dom'

import {makeGetPerformanceById} from '../../../selectors'

import {destroy, showAlert} from '../../../actions'

import {fullURL} from '../../../utils'

import PerformanceFlier from '../../generics/flyers/PerformanceFlier'

import RSVPSheetDialog from '../rsvp/RSVPSheetDialog'
import RSVPInviteDialog from '../rsvp/RSVPInviteDialog'

import EventForm from '../form/EventForm'


import { 
  EditIconButton,
  ButtonGroupRound, 
  DeleteIconButton,
  ShareIconButton,
  StatsIconButton,
  QRCodeIconButton,
  GradientPlayButton,
} from '../../generics'



const styles = {
  buttonRow: css`
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    padding: 10px 0;
    gap: 15px;

    @media (max-width: 550px) {
      flex-wrap: nowrap;
      overflow-x: scroll;
      -ms-overflow-style: none;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }
  `,
  ticketContainer: css`
    height: 48px;
    width: 48px;
    border-radius: 23px;
    background: #262626;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  ticketIcon: css`
    display: inline-block;            
    font-size: 20px;
    transition: transform 0.3s ease; 
    will-change: transform;

    &:hover {
      transform: scale(1.2);
    }
  `,
  link: css`
    text-decoration: none;
    color: inherit;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  rsvpButton: css`
    color: #fff;
    cursor: pointer;
    border: none;
    font-size: 0.7rem;
  `,
}



const PerformanceButtons = ({
  performanceId,
  name,
  start,
  price,
  destroy,
  tickets,
  imageURL,
  videoURL,
  showAlert,
  permission,
  rsvpEnabled,
  originalImage
}) => {

  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const rsvpToken = searchParams.get('invitation_token')
  const rsvpQuery = searchParams.get('rsvp') === 'true'

  const [openEdit, setOpenEdit] = useState(false)
  const [openRsvp, setOpenRsvp] = useState(false)
  

  // Auto-open RSVP sheet if ?rsvp=true
  useEffect(() => {
    if (rsvpToken || rsvpQuery) {
      const timer = setTimeout(() => setOpenRsvp(true), 2000)
      return () => clearTimeout(timer)
    }
  }, [rsvpToken, rsvpQuery])


  const handleDelete = useCallback(async () => {
    try {
      await destroy(`/performances/${performanceId}`, 'PERFORMANCE')
      navigate(-1)
      showAlert('Performance deleted', 'info')
    } catch {
      showAlert("Couldn't delete performance", 'error')
    }
  }, [destroy, performanceId, navigate, showAlert])



  function closeRsvp() {
    setOpenRsvp(false)
  }


  function handleRsvpClick() {
    setOpenRsvp(true)
  }



  // Build action buttons based on permission
  const actionButtons = useMemo(() => {
    const buttons = []

    if (videoURL) {
      buttons.push(<GradientPlayButton key='play' />)
    }

    if (rsvpEnabled || ['owner', 'editor'].includes(permission)) {
      buttons.push(
        <button key='rsvp' css={styles.rsvpButton} onClick={handleRsvpClick}>
          RSVP
        </button>
      )
    }

    if (['owner', 'editor'].includes(permission)) {
      buttons.push(
        <EditIconButton key='edit' open={openEdit} setOpen={setOpenEdit} type='performance'>
          <EventForm performanceId={performanceId} close={() => setOpenEdit(false)} />
        </EditIconButton>
      )
    }

    buttons.push(
      <ShareIconButton key='share' id={performanceId} type='performance' />,
      <StatsIconButton key='stats' id={performanceId} type='performance' />,
      <QRCodeIconButton key='qr'>
        <PerformanceFlier id={performanceId} image={originalImage} start={start} />
      </QRCodeIconButton>
    )

    if (permission === 'owner') {
      buttons.push(
        <DeleteIconButton
          key='delete'
          imageURL={imageURL}
          title='Delete Performance'
          text='Are you sure you want to delete this performance?'
          primaryAction={handleDelete}
        />
      )
    }

    return buttons
  }, [
    start,
    imageURL,
    videoURL,
    openEdit,
    permission,
    rsvpEnabled,
    handleDelete,
    performanceId,
    originalImage
  ])



  return (
    <div css={styles.buttonRow}>
      <ButtonGroupRound>
        {tickets &&
          <div css={styles.ticketContainer}>
            <a css={styles.link} href={fullURL(tickets)} target='_blank' rel='noopener noreferrer'>
              <span css={styles.ticketIcon}>🎫</span>
            </a>
          </div>
        }

        {actionButtons}
      </ButtonGroupRound>


      {['owner', 'editor'].includes(permission) 
        ? (
          <RSVPInviteDialog 
            eventID={performanceId} 
            eventName={name}
            open={openRsvp}
            onClose={closeRsvp}
          />
        ) : (
          <RSVPSheetDialog 
            eventID={performanceId}
            rsvpToken={rsvpToken}
            open={openRsvp}
            onClose={closeRsvp}
          />
        )}
    </div>
  )
}




const makeMapStateToProps = () => {
  const getPerformanceById = makeGetPerformanceById()

  return (state, {performanceId}) => {
    const { attributes: perf } = getPerformanceById(state, performanceId)
    const role = state.user?.attributes?.role
    const permission = ['admin', 'editor'].includes(role) ? 'owner' : perf.permission

    return {
      name: perf.name,
      start: perf.start,
      price: perf.price,
      tickets: perf.tickets,
      imageURL: perf.imageDerivatives.small,
      videoURL: perf.video,
      rsvpEnabled: perf.rsvpEnabled,
      originalImage: perf.image,
      permission,
    }
  }
}


const actions = {destroy, showAlert}


export default connect(makeMapStateToProps, actions)(PerformanceButtons)

